import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { ErrorLabel } from '../ErrorLabel';
import { Icon, AvailableIcons } from '../Icon';
import { DropDownValue, DropDownProps } from './types';
import { useDropDown, itemHeight, maxNumOfItems } from './useDropDown';
import './DropDown.scss';
import { EllipsisWithTooltip } from '../EllipsisWithToolTip';
import { Typography, TypographyType, TypographyVariant } from '../Typography';

export const DropDown = <T extends DropDownValue = DropDownValue>({
  label,
  placeholder,
  disabled,
  options,
  borderless,
  onChange,
  onOpen,
  className,
  value,
  error,
}: DropDownProps<T>): JSX.Element => {
  const dropDownLogic = useDropDown({
    value,
    options,
    onOpen,
    onChange,
    placeholder,
    disabled,
  });

  const {
    popupIsOpened,
    popupDimensions,
    popupRef,
    inputRef,
    onPopUpItemClick,
    switchPopUpOpen,
    inputValue,
    inputId,
    selectedOption,
  } = dropDownLogic;

  const component = (
    <ul
      className='syte-drop-down-popup'
      style={{ ...popupDimensions, maxHeight: maxNumOfItems * itemHeight }}
      ref={popupRef}
    >
      {options?.map(option => {
        const onItemClick = () => {
          if (option.isDisabled) return;
          onPopUpItemClick(option);
        };

        return (
          <li
            className={classNames('syte-drop-down-popup-item', {
              selected: selectedOption?.value === option.value,
              disabled: !!option?.isDisabled,
            })}
            key={option.value?.toString() ?? 'empty-key'}
            onClick={onItemClick}
            style={{ minHeight: itemHeight }}
          >
            <span className='syte-drop-down-popup-item-text'>
              <EllipsisWithTooltip
                tooltipPosition='right center'
                tooltipText={option.text}
                tooltipClassName='syte-drop-down-option-tooltip'
              >
                {option.text === 'Visual Search' ? 'Camera Search' : option.text}
              </EllipsisWithTooltip>
            </span>
          </li>
        );
      })}
    </ul>
  );

  const popUpComponent = popupIsOpened
    ? ReactDOM.createPortal(
        popupIsOpened ? component : null,
        document.querySelector('[id="root"], body') as Element
      )
    : null;

  return (
    <div
      className={classNames('syte-drop-down', className, { disabled })}
      onClick={switchPopUpOpen}
    >
      {label && (
        <label className='syte-field-label' htmlFor={inputId}>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
            {label}
          </Typography>
        </label>
      )}
      <div
        className={classNames('syte-drop-down-input-wrapper syte-input-base', {
          borderless,
          disabled,
          error,
        })}
        ref={inputRef}
      >
        <span className='syte-drop-down-input-wrapper-input' id={inputId}>
          <EllipsisWithTooltip className='syte-drop-down-input-tooltip' tooltipText={inputValue}>
            {inputValue === 'Visual Search' ? 'Camera Search' : inputValue}
          </EllipsisWithTooltip>
        </span>
        <div className='syte-drop-down-popup-arrow'>
          <Icon
            className={classNames('syte-drop-down-popup-arrow-icon', {
              opened: popupIsOpened,
            })}
            name={AvailableIcons.Back}
          />
        </div>
      </div>
      <ErrorLabel value={error} />
      {popUpComponent}
    </div>
  );
};
